/**
 * VDL Parsing Report API service
 */

import { apiClient } from './api'

export interface VDLParsingReportItem {
  id: number
  crm_id: number
  project_name: string | null
  vdl_manager_ids: number[] | null
  vdl_manager_names: string[] | null
  parse_phones: boolean
  parse_ishod: boolean
  phone_on: number
  phone_off: number
  ishod_on: number
  ishod_off: number
  operators: Record<string, boolean> | null
  has_source_issue: boolean
  has_ops_off: boolean
}

export interface VDLParsingReportDeltas {
  new_problems: Array<{ crm_id: number; name: string; phone_off: number; ishod_off: number }>
  fixed: Array<{ crm_id: number; name: string }>
  worsened: Array<{ crm_id: number; name: string; prev_off: number; curr_off: number }>
  improved: Array<{ crm_id: number; name: string; prev_off: number; curr_off: number }>
  delta_phone_off: number
  delta_ishod_off: number
  delta_projects_with_issues: number
  prev_date: string | null
  prev_datetime?: string | null
  prev_report_id?: number | null
}

export interface VDLParsingReport {
  id: number
  report_date: string
  report_datetime?: string | null
  run_type: string
  triggered_by?: string | null
  status: string
  total_active_projects: number
  projects_with_parse_flags: number
  projects_with_issues: number
  phone_on_total: number
  phone_off_total: number
  ishod_on_total: number
  ishod_off_total: number
  operator_summary: Record<string, { on: number; off: number }> | null
  manager_summary: Record<string, { total: number; with_issues: number }> | null
  deltas: VDLParsingReportDeltas | null
  duration_seconds: number | null
  created_at: string | null
  items: VDLParsingReportItem[] | null
}

export interface VDLParsingReportListItem {
  id: number
  report_date: string
  report_datetime?: string | null
  run_type?: string | null
  triggered_by?: string | null
  status: string
  total_active_projects: number
  projects_with_parse_flags: number
  projects_with_issues: number
  phone_off_total: number
  ishod_off_total: number
  deltas: VDLParsingReportDeltas | null
}

const vdlParsingReportService = {
  async getLatest(): Promise<VDLParsingReport> {
    const { data } = await apiClient.get('/vdl-parsing-report/latest')
    return data
  },

  async getList(days = 90): Promise<VDLParsingReportListItem[]> {
    const { data } = await apiClient.get('/vdl-parsing-report/list', { params: { days } })
    return data
  },

  async getByDate(date: string): Promise<VDLParsingReport[]> {
    const { data } = await apiClient.get(`/vdl-parsing-report/by-date/${date}`)
    // Backend returns List[VDLParsingReport] after multi-reports-per-day change.
    // Defensive: normalize single-object legacy response to array.
    if (Array.isArray(data)) return data
    if (data) return [data]
    return []
  },

  async getDetail(reportId: number): Promise<VDLParsingReport> {
    const { data } = await apiClient.get(`/vdl-parsing-report/detail/${reportId}`)
    return data
  },

  async runReport(targetDate?: string): Promise<{ status: string; report_id: number | null; message: string }> {
    const { data } = await apiClient.post('/vdl-parsing-report/run', null, {
      params: targetDate ? { target_date: targetDate } : undefined,
    })
    return data
  },
}

export default vdlParsingReportService
